import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import type { CSSProperties } from 'react'
import {
  IconArrowLeft,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconX,
  IconBook2,
  IconCash,
  IconFileCheck,
  IconFileInvoice,
  IconHandFinger,
  IconHierarchy2,
  IconBuildingHospital,
  IconCircleDashed,
  IconCoin,
  IconCoinOff,
  IconId,
  IconLayoutDashboard,
  IconListDetails,
  IconLock,
  IconScale,
  IconMailForward,
  IconReceipt2,
  IconRepeat,
  IconStack2,
  IconTrophy,
  IconUser,
  IconUserX,
  IconTimeline,
  type TablerIcon,
} from '@tabler/icons-react'
import { useDataset } from '@/data/DatasetProvider'
import { useAppData } from '@/data/AppData'
import { ClearFiltersButton, FilterBar } from './FilterBar'
import { ManagementToggle } from './ManagementToggle'
import { ClaimBasisToggle } from './ClaimBasisToggle'
import { weightedTile } from '@/data/weightsNote'
import { useMediaQuery } from '@/ui/useMediaQuery'
import { usePreferences, type RailMode } from '@/ui/preferences'
import { UserMenu } from './UserMenu'
import { GlobalSearch } from './GlobalSearch'
import { ViewAsControl } from './ViewAs'
import { PortalBar, usePortalBarReady } from './PortalBar'
import { useAccess } from '@/access/AccessContext'
import { PageLimitedNotice, pathAllowed, usePageLimit } from '@/access/pageLimit'
import { shownTools, useDevMode } from '@/ui/devMode'
import { viewingAs } from '@/data/viewAs'
import { useAuth } from '@/auth/AuthProvider'
import { ThemeControl } from './ThemeControl'
import { CopyLinkButton, ViewLinks } from '@/state/ViewLinks'
import { CommentsButton, CommentsPanel } from '@focalpointcare/review-drawer'
import { setPageLabel } from '@/comments/reviewHost'
import { Absent } from './Bits'
import type { GrainKey } from '@/data/coverage'
import { money2, int, money0, monthSpan, pct } from '@/format'
import { poolCollection } from '@/measures/core'
import { COLLECTION_LABEL, describeWindow } from '@/measures/collection'
import { backFallback } from '@/ui/backFallback'

/**
 * Page order is the spec's order. The personal view is named for the reader, per
 * Shayna on 2026-09-23. "My team's metrics" became the team view of My metrics on
 * 2026-10-05 (review #59); /supervisor-view redirects there so bookmarks still work.
 */
export const PAGES = [
  { path: '/', label: 'Overview', end: true },
  { path: '/employee-view', label: 'My metrics' },
  { path: '/employees', label: 'Employees' },
  { path: '/scorecard', label: 'Scorecard' },
  { path: '/supervisors', label: 'Supervisors' },
  { path: '/facilities', label: 'Facilities' },
  { path: '/backlog', label: 'Backlog & recency' },
  { path: '/touch', label: 'Touch profile' },
  // After Touch profile, as Shayna asked (2026-10-18).
  { path: '/touch-drivers', label: 'Touch drivers' },
  { path: '/claim-detail', label: 'Claim detail' },
  { path: '/touch-detail', label: 'Touch detail' },
  { path: '/worklist-assignments', label: 'Worklist assignments' },
  { path: '/terminated', label: 'Terminated worklists' },
  { path: '/payroll-without-claims', label: 'Payroll without claims' },
  { path: '/method', label: 'Data & method' },
] as const

/**
 * Each tab's icon, by path (Tabler, outline). Kept beside PAGES rather than in it so
 * PAGES stays plain data for anything else that lists the pages.
 */
const PAGE_ICONS: Record<string, TablerIcon> = {
  '/': IconLayoutDashboard,
  '/employee-view': IconUser,
  '/employees': IconId,
  '/scorecard': IconTrophy,
  '/supervisors': IconHierarchy2,
  '/facilities': IconBuildingHospital,
  '/backlog': IconStack2,
  '/touch': IconHandFinger,
  '/touch-drivers': IconRepeat,
  '/claim-detail': IconFileInvoice,
  '/touch-detail': IconListDetails,
  '/terminated': IconUserX,
  '/worklist-assignments': IconTimeline,
  '/payroll-without-claims': IconCoinOff,
  '/method': IconBook2,
  '/access': IconLock,
  '/rules': IconScale,
  '/statements': IconMailForward,
}

/**
 * The rail's groups, in PAGES order. The split is ours (2026-09-30), not the spec's:
 * pages about people's figures, pages about the claims themselves, and reference.
 * Overview stands alone above them. A page missing here still shows, at the end.
 */
const RAIL_GROUPS: { label: string | null; paths: string[] }[] = [
  { label: null, paths: ['/'] },
  {
    label: 'People',
    paths: ['/employee-view', '/employees', '/scorecard', '/supervisors'],
  },
  { label: 'Claims', paths: ['/facilities', '/backlog', '/touch', '/touch-drivers', '/claim-detail', '/touch-detail', '/worklist-assignments', '/terminated', '/payroll-without-claims'] },
  { label: 'Reference', paths: ['/method', '/access', '/rules', '/statements'] },
]

type ExtraPage = { path: string; label: string }
type NavPage = ExtraPage & { end?: boolean }

/**
 * Pages only some people get, after PAGES: Access for owners of the access groups, and
 * Rules for app admins and Statements for full access (neither while viewing as someone).
 * Not gated by Dev mode.
 */
function extraPages(owner: boolean, rules: boolean, statements: boolean): ExtraPage[] {
  return [
    ...(owner ? [{ path: '/access', label: 'Access' }] : []),
    ...(rules ? [{ path: '/rules', label: 'Rules' }] : []),
    ...(statements ? [{ path: '/statements', label: 'Statements' }] : []),
  ]
}

/**
 * Admin pages: settings, not figures. They render without the masthead figures, the
 * filter bar, Include management and the Claims switch, none of which apply to them
 * (Rules review, 2026-10-06). Add a path here for any future settings page.
 */
export const ADMIN_PATHS: ReadonlySet<string> = new Set(['/rules', '/statements'])

/** Widths of the wide-screen rail, by mode. */
const RAIL_WIDTH: Record<RailMode, number> = { full: 212, icons: 60, hidden: 0 }

function railLinkStyle(isActive: boolean, icons: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: icons ? 'center' : undefined,
    gap: 9,
    fontSize: 13,
    padding: icons ? '8px 0' : '7px 10px',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    borderRadius: '0 7px 7px 0',
    color: isActive ? 'var(--accent-ink)' : 'var(--ink-2)',
    fontWeight: isActive ? 600 : 400,
    background: isActive ? 'var(--accent-soft)' : 'transparent',
    borderLeft: `2px solid ${isActive ? 'var(--accent)' : 'transparent'}`,
    transition: 'background 120ms ease, color 120ms ease',
  }
}

const railBtn: CSSProperties = {
  display: 'grid',
  placeItems: 'center',
  width: 30,
  height: 30,
  padding: 0,
  border: '1px solid var(--rule)',
  borderRadius: 7,
  background: 'var(--surface)',
  color: 'var(--ink-2)',
  cursor: 'pointer',
}

/**
 * The wide-screen page rail: the app's name, its pages in groups, when the data is
 * from, and the controls that shrink it to icons or put it away (remembered per
 * person, see preferences.tsx). In icons mode every page is still one click away, the
 * name on hover and to screen readers.
 */
function PageRail({ mode, pages }: { mode: 'full' | 'icons'; pages: readonly NavPage[] }) {
  const { setRailMode } = usePreferences()
  const { dataset } = useDataset()
  const icons = mode === 'icons'
  const byPath = new Map<string, NavPage>(pages.map((p) => [p.path, p]))
  const grouped = new Set(RAIL_GROUPS.flatMap((g) => g.paths))
  const groups = [
    ...RAIL_GROUPS,
    { label: null, paths: pages.map((p) => p.path).filter((p) => !grouped.has(p)) },
  ]
    .map((g) => ({ ...g, pages: g.paths.flatMap((p) => byPath.get(p) ?? []) }))
    .filter((g) => g.pages.length > 0)

  const asOf = dataset?.dataAsOf ?? dataset?.generatedAt
  const asOfText = asOf
    ? new Date(asOf).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      {!icons && (
        <div
          className="eyebrow"
          style={{ fontSize: 'var(--t-micro)', padding: '0 12px 10px', color: 'var(--ink-3)' }}
        >
          Cost per Claim
        </div>
      )}
      <div style={{ padding: icons ? '0 6px 8px' : '0 0 8px 12px' }}>
        <BackButton vertical compact={icons} />
      </div>
      <nav aria-label="Pages" style={{ display: 'grid', gap: 2 }}>
        {groups.map((g, i) => (
          <div key={g.label ?? `g${i}`} style={{ display: 'grid', gap: 2 }}>
            {i > 0 &&
              (g.label && !icons ? (
                <div
                  className="eyebrow"
                  aria-hidden="true"
                  style={{ fontSize: 'var(--t-micro)', padding: '12px 12px 4px', color: 'var(--ink-3)' }}
                >
                  {g.label}
                </div>
              ) : (
                <div aria-hidden="true" style={{ height: 1, background: 'var(--rule)', margin: '8px 10px' }} />
              ))}
            {g.pages.map((p) => {
              const Icon = PAGE_ICONS[p.path]
              return (
                <NavLink
                  key={p.path}
                  to={p.path}
                  end={p.end ?? false}
                  title={icons ? p.label : undefined}
                  aria-label={icons ? p.label : undefined}
                  style={({ isActive }) => railLinkStyle(isActive, icons)}
                >
                  {Icon && <Icon size={icons ? 18 : 16} stroke={1.75} aria-hidden="true" style={{ flex: 'none' }} />}
                  {!icons && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.label}</span>}
                </NavLink>
              )
            })}
          </div>
        ))}
      </nav>

      <div
        style={{
          marginTop: 'auto',
          paddingTop: 16,
          display: 'grid',
          gap: 10,
          justifyItems: icons ? 'center' : 'start',
          paddingLeft: icons ? 0 : 12,
        }}
      >
        {asOfText && !icons && (
          <div style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)', lineHeight: 1.4 }}>
            Data as of
            <br />
            <span className="mono" style={{ color: 'var(--ink-2)' }}>{asOfText}</span>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: icons ? 'column' : 'row', gap: 6 }}>
          <button
            type="button"
            style={railBtn}
            onClick={() => setRailMode(icons ? 'full' : 'icons')}
            aria-label={icons ? 'Show page names' : 'Shrink to icons'}
            title={icons ? 'Show page names' : 'Shrink to icons'}
          >
            {icons ? <IconLayoutSidebarLeftExpand size={17} stroke={1.75} /> : <IconLayoutSidebarLeftCollapse size={17} stroke={1.75} />}
          </button>
          <button
            type="button"
            style={railBtn}
            onClick={() => setRailMode('hidden')}
            aria-label="Hide the page list"
            title="Hide the page list"
          >
            <IconX size={17} stroke={1.75} />
          </button>
        </div>
      </div>
    </div>
  )
}

/** Brings a hidden rail back: a small tab on the window's left edge. */
function RailReopen() {
  const { setRailMode } = usePreferences()
  return (
    <button
      type="button"
      onClick={() => setRailMode('full')}
      aria-label="Show the page list"
      title="Show the page list"
      style={{
        position: 'fixed',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 30,
        display: 'grid',
        placeItems: 'center',
        width: 26,
        height: 56,
        padding: 0,
        border: '1px solid var(--rule)',
        borderLeft: 0,
        borderRadius: '0 8px 8px 0',
        background: 'var(--surface)',
        color: 'var(--ink-2)',
        boxShadow: 'var(--shadow)',
        cursor: 'pointer',
      }}
    >
      <IconLayoutSidebarLeftExpand size={16} stroke={1.75} />
    </button>
  )
}

/**
 * The five running figures, reflecting the current scope. Labels are the Ledger's.
 *
 * Since Shayna's 2026-10-02 review this is the ONE summary row: the Overview's own
 * strip repeated these figures under them and was removed (#8), and Open backlog gave
 * its place to collection rate and 0% touch share (#7). The pile has its own page,
 * Backlog & recency.
 *
 * Two layouts, both under the title and ruled off from it, never beside it:
 *   below   one row of five, divided by hairlines — any screen 900px and up.
 *   grid    phone width: a two-column grid.
 *
 * There was a third, the row beside the title. With five figures, the comments button
 * and the theme switch it left the title block a hundred-odd pixels even at 1920, the
 * title in three lines and the subtitle a tall column (Shayna's review #44,
 * 2026-10-02), so the title now always has the masthead's full width.
 */
function Masthead({ layout }: { layout: 'below' | 'grid' }) {
  const narrow = layout === 'grid'
  const { scope, coverage, derived, claimBasis, weights } = useAppData()
  const people = scope.people

  const claimLabor = people.reduce((a, p) => a + p.claimLabor, 0)
  const billed = people.reduce((a, p) => a + p.claims, 0)
  const cpc = billed === 0 ? null : claimLabor / billed
  // SUM(resolved charge) / SUM(charge) over the people who have a basis — a missing
  // rate is not a zero. Each person's basis is already the range's lagged window
  // (measures/collection.ts), so this is the org over the same bill months.
  const collWindow = derived.collectionWindow
  const collection = poolCollection(people.map((p) => p.collection))
  // Over the three touch buckets, not resolved claims: a different date basis.
  const noTouch = people.reduce((a, p) => a + p.noTouch, 0)
  const touchBase = people.reduce((a, p) => a + p.touchBase, 0)

  const figs: {
    label: string
    value: string
    icon: TablerIcon
    absent?: GrainKey
    title?: string
  }[] = [
    {
      label: 'Cost per claim',
      value: money2(cpc),
      icon: IconReceipt2,
      title: 'All payroll labor over all billed claims',
    },
    claimBasis === 'weighted'
      ? { ...weightedTile(weights), value: int(Math.round(billed)), icon: IconFileCheck }
      : { label: 'Billed claims', value: int(billed), icon: IconFileCheck },
    // The payroll cost per claim divides, not `payroll` (FactPayroll, a separately
    // built total with no month grain). Two payroll figures on one screen was the
    // confusion in the 2026-09-23 review; this one also follows the month range.
    {
      label: 'Payroll labor',
      value: money0(claimLabor),
      icon: IconCash,
      title: 'Everyone in scope, claims or not',
    },
    {
      label: COLLECTION_LABEL,
      value: pct(collection),
      icon: IconCoin,
      absent: coverage.collectedPct ? undefined : 'collectedPct',
      title:
        'Resolved charge over billed charge, for bills from the six months ending three months before the range (a longer range adds its months)' +
        (collWindow ? ` — ${describeWindow(collWindow)}` : ''),
    },
    {
      label: '0% touch share',
      value: pct(touchBase === 0 ? null : noTouch / touchBase),
      icon: IconCircleDashed,
      title: 'Claims resolved without being worked',
    },
  ]

  return (
    <dl
      aria-label="Figures for the current scope"
      style={
        narrow
          ? {
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px 20px',
              margin: '12px 0 0',
              paddingTop: 10,
              borderTop: '1px solid var(--rule)',
            }
          : {
              display: 'flex',
              flexWrap: 'wrap',
              rowGap: 10,
              margin: '12px 0 0',
              paddingTop: 12,
              borderTop: '1px solid var(--rule)',
            }
      }
    >
      {figs.map((f, i) => (
        <div
          key={f.label}
          title={f.title}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            // Hairline between figures, not around them; the first has nothing to
            // its left to divide from.
            borderLeft: !narrow && i > 0 ? '1px solid var(--rule)' : undefined,
            padding: narrow ? 0 : i === 0 ? '0 18px 0 0' : '0 18px',
          }}
        >
          {/* The figure's icon on a soft badge, the height of label + value. */}
          <span
            aria-hidden="true"
            style={{
              display: 'grid',
              placeItems: 'center',
              flex: 'none',
              width: 28,
              height: 28,
              borderRadius: 7,
              background: 'var(--accent-soft)',
              color: 'var(--accent)',
            }}
          >
            <f.icon size={17} stroke={1.75} />
          </span>
          <div style={{ display: 'grid', gap: 3, minWidth: 0 }}>
            <dt
              className="eyebrow"
              style={{ fontSize: 'var(--t-micro)', lineHeight: 1.2, whiteSpace: 'nowrap' }}
            >
              {f.label}
            </dt>
            <dd
              className="mono"
              style={{ margin: 0, fontSize: 14, fontWeight: 500, lineHeight: 1.2 }}
            >
              {f.absent ? <Absent grain={f.absent} /> : f.value}
            </dd>
          </div>
        </div>
      ))}
    </dl>
  )
}

/**
 * The months every figure below covers: the bundle's period, or the month range when
 * one is set. A range that changed the numbers but not this line would contradict them.
 */
function PeriodLabel({ fallback }: { fallback: string }) {
  const { derived } = useAppData()
  if (!derived.narrowed) return <>{fallback}</>
  return (
    <>
      {monthSpan(derived.months)} · of {monthSpan(derived.allMonths)}
    </>
  )
}

/** Says so, loudly, when the numbers on screen are invented. */
function DemoBanner() {
  if (import.meta.env.VITE_DEMO !== '1') return null
  return (
    <div
      style={{
        background: 'var(--crit-bg)',
        borderBottom: '1px solid var(--crit)',
        color: 'var(--crit-ink)',
        textAlign: 'center',
        padding: '6px 16px',
        fontSize: 'var(--t-micro)',
        fontFamily: 'var(--mono)',
      }}
    >
      DEMO BUILD — every figure below is synthetic. Not FCC data.
    </div>
  )
}

/**
 * Back, to wherever the reader was before — another tab, or the Overview slice they
 * drilled from. Asked for in the 2026-09-23 review ("we need back buttons"): a drill
 * through lands on a new tab with no way to return but the browser's own button.
 *
 * On the first page of a visit — a review link, a new tab — there is no page before,
 * so it goes to the page's default instead (review #601; ui/backFallback.ts): the page
 * without its picks, or the Overview. Hidden only on the bare Overview.
 *
 * "First" is React Router's own index of the entry in this tab (`idx` in history
 * state), not `location.key === 'default'`: the app replaces the entry it opens on
 * (the filter bar stamps it), which gives it a key, so the old test never fired and a
 * fresh tab's Back called navigate(-1) with nothing behind it and did nothing.
 */
function BackButton({ vertical = false, compact = false }: { vertical?: boolean; compact?: boolean }) {
  const location = useLocation()
  const navigate = useNavigate()
  const first = ((window.history.state as { idx?: number } | null)?.idx ?? 0) === 0
  const fallback = first ? backFallback(location.pathname, location.search) : null
  if (first && fallback === null) return null
  const where =
    fallback === null
      ? 'Back to the previous view'
      : fallback === '/'
        ? 'Back to the Overview'
        : `Back to all of ${PAGES.find((p) => p.path === fallback)?.label ?? 'this page'}`
  const go = () => (fallback === null ? navigate(-1) : navigate(fallback))
  if (compact)
    return (
      <button type="button" onClick={go} aria-label={where} title={where} style={railBtn} data-testid="back-button">
        <IconArrowLeft size={17} stroke={1.75} />
      </button>
    )
  return (
    <button
      type="button"
      onClick={go}
      aria-label={where}
      title={where}
      data-testid="back-button"
      style={{
        alignSelf: vertical ? 'start' : 'center',
        flex: '0 0 auto',
        ...(vertical ? { marginBottom: 8 } : { marginRight: 6 }),
        fontSize: 13,
        padding: '5px 10px',
        borderRadius: 6,
        border: '1px solid var(--rule-2)',
        background: 'var(--surface)',
        color: 'var(--ink-2)',
        cursor: 'pointer',
      }}
    >
      ← Back
    </button>
  )
}

/**
 * The page links as a scrolling tab row, at phone width (wide screens get PageRail).
 *
 * The active page is carried by a filled pill AND a 2px accent underline. An underline
 * alone is two pixels of colour on a row of otherwise identical text — easy to miss, and
 * invisible to anyone who cannot separate the accent from the ink.
 */
function PageNav({ pages }: { pages: readonly NavPage[] }) {
  return (
    <nav
      aria-label="Pages"
      style={{
        // One scrolling row rather than a wrapping one, so the row stays a single
        // line however many pages there are.
        display: 'flex',
        gap: 4,
        paddingTop: 6,
        overflowX: 'auto',
        scrollbarWidth: 'thin',
      }}
    >
      <BackButton />
      {pages.map((p) => {
        const Icon = PAGE_ICONS[p.path]
        return (
          <NavLink
            key={p.path}
            to={p.path}
            end={p.end ?? false}
            style={({ isActive }) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              padding: '8px 13px 9px',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              borderRadius: '7px 7px 0 0',
              color: isActive ? 'var(--accent-ink)' : 'var(--ink-2)',
              fontWeight: isActive ? 600 : 400,
              background: isActive ? 'var(--accent-soft)' : 'transparent',
              borderBottom: `2px solid ${isActive ? 'var(--accent)' : 'transparent'}`,
              transition: 'background 120ms ease, color 120ms ease',
            })}
          >
            {Icon && <Icon size={16} stroke={1.75} aria-hidden="true" style={{ flex: 'none' }} />}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.label}</span>
          </NavLink>
        )
      })}
    </nav>
  )
}

export function AppShell() {
  const { dataset } = useDataset()
  const wide = useMediaQuery('(min-width: 900px)')
  const { owner } = useAccess()
  const dev = useDevMode()
  // A page-limited reader (access/pageLimit.tsx) gets their pages and nothing else.
  const allowed = usePageLimit()
  const tools = shownTools(dev.features, dev.on)
  const extra = allowed ? [] : extraPages(owner === 'owner', tools.rules && !viewingAs(), tools.statements && !viewingAs())
  const navPages: NavPage[] = [...PAGES, ...extra].filter((p) => pathAllowed(allowed, p.path))
  const home = allowed?.[0] ?? '/'
  const { account } = useAuth()
  const barReady = usePortalBarReady()
  const { railMode } = usePreferences()
  // On My metrics the figures are one person's or one team's, and each is on the page
  // below: the rates and billed claims in its measures, payroll labor in the hero. The
  // masthead repeating them was the duplicative summary; the page is the summary. Since
  // the page opens on whoever is signed in (review #59), that is true before anyone is
  // picked too.
  const { pathname } = useLocation()
  const admin = ADMIN_PATHS.has(pathname)
  // Nor for a page-limited reader: the figures are the pages they do not have.
  const showFigures = pathname !== '/employee-view' && !admin && !allowed
  // Any other route — a deep link, a search hit, Back — says what they have instead.
  // The bare address, where sign-in lands, opens their first page.
  const page =
    allowed && !pathAllowed(allowed, pathname) ? (
      pathname === '/' && allowed[0] ? (
        <Navigate to={allowed[0]} replace />
      ) : (
        <PageLimitedNotice allowed={allowed} />
      )
    ) : (
      <Outlet />
    )
  // What a review comment calls this page.
  const pageLabel =
    [...PAGES, ...extraPages(true, true, true)].find((p) => p.path === pathname)?.label ?? pathname
  useEffect(() => setPageLabel(pageLabel), [pageLabel])

  const masthead = (
      <header
        style={{
          background: "var(--surface)",
          // 4px ocean rule across the top, per Shayna.
          borderTop: "4px solid var(--ocean)",
        }}
      >
        <div className="page" style={{ paddingTop: 12, paddingBottom: 14 }}>
          {/*
            Bottom-aligned, as in the Ledger: the controls sit on the subtitle's
            line, so the title row has one baseline rather than things centred at
            different heights. The logo alone stays
            centred on the title block — it is a mark, not a line of text.
          */}
          <div style={{ display: 'flex', alignItems: wide ? 'flex-end' : 'center', gap: 16 }}>
            {/*
              The FCC lockup, same asset the Power BI reports use and the same themed
              swap as the sign-in card. Sized to the title block rather than set as a
              fixed square, so it stays optically level with the wordmark beside it.
              Smaller below 900px, where the row is title + controls and a 64px mark
              pushes the title into a second line for no information.
            */}
            {/*
              Top-left and clickable, which is where every reader already expects a
              logo to take them home. `end` so it only lights up on the Overview
              itself rather than on every route beneath it.
            */}
            <NavLink
              to={home}
              end
              aria-label="Claims Efficiency Ledger — overview"
              className="brand-home"
              style={{ alignSelf: 'center' }}
            >
              <div
                className="brand-mark"
                role="img"
                aria-label="Future Care Consultants"
                style={{ width: wide ? 64 : 40, height: wide ? 64 : 40 }}
              />
            </NavLink>
            {/* flex:1 + minWidth:0 keeps the figures and controls on this row rather
                than letting a long eyebrow wrap them onto their own line. */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <span
                className="eyebrow"
                style={{
                  fontSize: 'var(--t-micro)',
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {wide ? 'Revenue cycle management · ' : ''}
                <PeriodLabel fallback={`${dataset?.periodStart}–${dataset?.periodEnd}`} />
              </span>
              <h1
                style={{
                  fontFamily: 'var(--head)',
                  fontSize: 20,
                  fontWeight: 500,
                  letterSpacing: '-0.01em',
                  margin: '1px 0 0',
                  lineHeight: 1.2,
                }}
              >
                Claims Efficiency Ledger
              </h1>
              {/* Not at phone width, where it stood as a five-line column beside the
                  logo and pushed the figures off the first screen (review #44). */}
              {wide && (
                <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--ink-2)' }}>
                  Cost per billed claim, throughput, quality and backlog by employee, job
                  title, supervisor and department.
                </p>
              )}
            </div>
            {/*
              The controls stand apart from the title block, on its right.

              The theme switch is desktop-only: at phone width its 90px would break the
              title into three lines, and the account menu carries the same setting.
            */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginLeft: wide ? 28 : 'auto',
                flex: '0 0 auto',
              }}
            >
              {/* Find a person, facility, payer, page… (review #403). Ctrl+K or "/". */}
              {wide && <GlobalSearch pages={navPages} limited={!!allowed} />}
              {/* Every page with a filter bar: the bar and the page's own picks, cleared. */}
              {!admin && <ClearFiltersButton />}
              {/* Everyone: a link opens this view within the reader's own access. */}
              {!admin && <CopyLinkButton />}
              {/* Full-access callers only; renders nothing for everyone else. */}
              <ViewAsControl />
              {/* Reviewers only; renders nothing for everyone else. */}
              <CommentsButton />
              {wide && <ThemeControl />}
              {/* One account disc: once the portal bar has loaded and someone is signed
                  in, the bar's disc (with theme/density slotted in) replaces this one.
                  Until then — or if the portal is unreachable — this one stays. */}
              {!(barReady && account) && <UserMenu />}
            </div>
          </div>
          {/* Its own row at phone width: among the buttons it pushed the title into the logo. */}
          {!wide && <GlobalSearch pages={navPages} limited={!!allowed} block />}
          {showFigures && <Masthead layout={wide ? 'below' : 'grid'} />}
        </div>
      </header>
  )

  return (
    <>
      {/* The FCC Portal bar, above everything; nothing at all without VITE_PORTAL_URL. */}
      <PortalBar />
      <DemoBanner />
      <CommentsPanel />

      {wide ? (
        /*
          Wide: an app shell. The FCC Portal bar is global; this app's pages are a
          full-height rail flush against the window's left edge, every one in view at
          once — no scrolling row, no "More" menu (both rejected 2026-09-30: seeing
          the rest should take no work). The title, figures, filters and page all sit
          to its right; the rail stays put while they scroll.
        */
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `${RAIL_WIDTH[railMode]}px minmax(0, 1fr)`,
          }}
        >
          {railMode === 'hidden' ? (
            <>
              <div />
              <RailReopen />
            </>
          ) : (
            <aside
              aria-label="Cost per Claim pages"
              style={{
                // Under the pinned FCC Portal bar when there is one (PortalBar sets
                // --shell-top), so the whole rail, controls included, is on screen.
                position: 'sticky',
                top: 'var(--shell-top, 0px)',
                alignSelf: 'start',
                height: 'calc(100vh - var(--shell-top, 0px))',
                overflowY: 'auto',
                padding: railMode === 'icons' ? '14px 0 16px' : '14px 10px 16px 0',
                background: 'var(--surface-2)',
                borderRight: '1px solid var(--rule)',
              }}
            >
              <PageRail mode={railMode} pages={navPages} />
            </aside>
          )}
          <div style={{ minWidth: 0 }}>
            {masthead}
            {!admin && <FilterBar />}
            <main
              className="page"
              style={{ paddingTop: 16, paddingBottom: 48, borderTop: '1px solid var(--rule)' }}
            >
              {/* On the page, not in the filter bar: who the figures count is part of
                  reading them. See ManagementToggle. */}
              {/* Opens a shared link's view, and says who in it is outside your access. */}
              <ViewLinks />
              {!admin && <ManagementToggle />}
              {!admin && !allowed && <ClaimBasisToggle />}
              {page}
            </main>
            <Footer />
          </div>
        </div>
      ) : (
        <>
          {masthead}
          {!admin && <FilterBar />}
          {/* Narrow: tabs under the filters, as in the Ledger, pinned while the page
              scrolls so switching pages never means scrolling back up. */}
          <div
            style={{
              position: 'sticky',
              top: 'var(--shell-top, 0px)',
              zIndex: 20,
              background: 'var(--surface)',
              borderBottom: '1px solid var(--rule)',
            }}
          >
            {/* Title, filters and tabs are one surface, as in the Ledger; a single
                rule separates the tabs from the filters above them. */}
            <div
              className="page"
              style={{ paddingTop: 0, paddingBottom: 0, borderTop: '1px solid var(--rule)' }}
            >
              <PageNav pages={navPages} />
            </div>
          </div>

          <main className="page" style={{ paddingTop: 16, paddingBottom: 48 }}>
            <ViewLinks />
            {!admin && <ManagementToggle />}
            {!admin && !allowed && <ClaimBasisToggle />}
            {page}
          </main>
          <Footer />
        </>
      )}
    </>
  )
}

/**
 * The footer. Deliberately short.
 *
 * "Built <date>" is the only diagnostic worth a reader's attention — two people
 * quoting different figures is nearly always two different builds. The bundle
 * version and the cache state are for us, not for them, and they were on screen
 * for a while reading like a fault code.
 */
function Footer() {
  const { dataset } = useDataset()
  // When the DATA was loaded, not when the refresh ran: after a missed nightly load the
  // refresh still runs and republishes yesterday's figures, and a run date would call
  // them current. Bundles built before dataAsOf existed fall back to the run date.
  const asOf = dataset?.dataAsOf ?? dataset?.generatedAt
  const built = asOf
    ? new Date(asOf).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  return (
    <footer style={{ borderTop: '1px solid var(--rule)', background: 'var(--surface-2)' }}>
      <div
        className="page"
        style={{
          paddingTop: 16,
          paddingBottom: 20,
          display: 'flex',
          gap: 14,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 'var(--t-rank)', color: 'var(--ink-2)' }}>
          Future Care Consultants
          {built && (
            <span style={{ color: 'var(--ink-3)' }}>
              {' '}
              · {dataset?.dataAsOf ? 'Data as of' : 'Updated'} {built}
            </span>
          )}
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 'var(--t-rank)',
            color: 'var(--ink-3)',
          }}
        >
          Confidential — individual pay and performance.
        </span>
      </div>
    </footer>
  )
}
